// src/components/GlobalSearchModal.js
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, FileText, User, Loader } from 'lucide-react';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import './GlobalSearchModal.css';

export default function GlobalSearchModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const searchInputRef = useRef();
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    loading,
    clearSearch
  } = useGlobalSearch();

  // Clear search when modal closes
  useEffect(() => {
    if (!isOpen) {
      clearSearch();
    }
  }, [isOpen, clearSearch]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      // Multiple attempts to ensure focus works
      const focusInput = () => {
        const input = searchInputRef.current;
        if (input) {
          // Force focus and make sure it's ready for input
          input.focus();
          input.click(); // Sometimes needed on mobile
          // Set cursor position
          if (input.setSelectionRange) {
            input.setSelectionRange(0, 0);
          }
        }
      };

      // Immediate attempt
      requestAnimationFrame(focusInput);
      
      // Backup attempts
      const timeoutId1 = setTimeout(focusInput, 100);
      const timeoutId2 = setTimeout(focusInput, 250);
      
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
      };
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleResultClick = (type, id) => {
    clearSearch();
    onClose();
    if (type === 'order') {
      navigate(`/orders/${id}`);
    } else if (type === 'client') {
      navigate(`/clients/${id}`);
    }
  };

  if (!isOpen) return null;

  const hasResults = searchResults.orders.length > 0 || searchResults.clients.length > 0;
  const showNoResults = searchQuery.trim() && !hasResults && !loading;

  return (
    <div className="global-search-overlay" onClick={onClose}>
      <div className="global-search-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="search-modal-header">
          <div className="search-modal-title">
            <Search size={20} />
            <span>Global Search</span>
          </div>
          <button className="search-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Search Input */}
        <div className="search-input-container">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search anything.."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => console.log('Input focused')} // Debug
            onBlur={() => console.log('Input blurred')} // Debug
            className="global-search-input"
            autoComplete="off"
            autoFocus
            tabIndex={0}
            spellCheck={false}
          />
          {loading && (
            <div className="search-loading">
              <Loader size={16} className="spinner" />
            </div>
          )}
        </div>

                  {/* Clients Results */}
          {searchResults.clients.length > 0 && (
            <div className="search-results-section">
              <h4 className="results-section-title">
                <User size={16} />
                Clients ({searchResults.clients.length})
              </h4>
              <div className="results-list">
                {searchResults.clients.map(client => (
                  <div 
                    key={client.id} 
                    className="result-item"
                    onClick={() => handleResultClick('client', client.id)}
                  >
                    <div className="result-main">
                      <span className="result-title">{client.name || 'Unnamed Client'}</span>
                      <span className="result-subtitle">
                        {client.city && client.state 
                          ? `${client.city}, ${client.state}` 
                          : client.city || client.state || 'Location not specified'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Results */}
        <div className="search-results-container">
          {/* Orders Results */}
          {searchResults.orders.length > 0 && (
            <div className="search-results-section">
              <h4 className="results-section-title">
                <FileText size={16} />
                Orders ({searchResults.orders.length})
              </h4>
              <div className="results-list">
                {searchResults.orders.map(order => (
                  <div 
                    key={order.id} 
                    className="result-item"
                    onClick={() => handleResultClick('order', order.id)}
                  >
                    <div className="result-main">
                      <span className="result-title">#{order.jobNumber || order.id}</span>
                      <span className="result-subtitle">{order.dealershipName || 'Unknown Dealership'}</span>
                    </div>
                    <span className={`result-status ${(order.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                      {order.status || 'Unknown Status'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {showNoResults && (
            <div className="search-no-results">
              <Search size={32} className="no-results-icon" />
              <h3>No results found</h3>
              <p>No orders or clients match "{searchQuery}"</p>
              <small>Try searching by job number, dealership name, client name, or location</small>
            </div>
          )}

          {/* Search Tips */}
          {!searchQuery.trim() && !loading && (
            <div className="search-tips">
              <h4>Search Tips</h4>
              <ul>
                <li><strong>Orders:</strong> Search by job number, dealership name, or status</li>
                <li><strong>Clients:</strong> Search by name, city, or state</li>
                <li><strong>Quick navigation:</strong> Click any result to go directly to that page</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}